import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, map} from 'rxjs';
import {ApiResponse} from '../../auth/auth.models';
import {buildApiUrl} from '../api/api-url';
import {AppLanguageService} from '../i18n/app-language.service';
import {CatalogCacheService} from '../catalogs/catalog-cache.service';

export type CurrencyDto = {code: string; name: string; shortName: string; minorUnits: number; isActive: boolean};
export type CurrencyPreferencesDto = {currencies: CurrencyDto[]; workingCurrencyCodes: string[]};
export type CurrencyCatalogDto = {currencies: CurrencyDto[]};
export type WorkingCurrencyCodesDto = {workingCurrencyCodes: string[]};

function isCurrency(value: unknown): value is CurrencyDto {
  if (!value || typeof value !== 'object') return false;
  const dto = value as Partial<CurrencyDto>;
  return typeof dto.code === 'string' && /^[A-Z]{3}$/.test(dto.code)
    && typeof dto.name === 'string' && dto.name.trim().length > 0
    && typeof dto.shortName === 'string' && dto.shortName.trim().length > 0
    && typeof dto.minorUnits === 'number' && Number.isInteger(dto.minorUnits) && dto.minorUnits >= 0 && dto.minorUnits <= 4
    && typeof dto.isActive === 'boolean';
}

function isCurrencyCatalog(value: unknown): value is CurrencyCatalogDto {
  if (!value || typeof value !== 'object' || !('currencies' in value)) return false;
  const rows = value.currencies;
  return Array.isArray(rows) && rows.every(isCurrency) && new Set(rows.map(row => row.code)).size === rows.length;
}
export type ItemPriceDto = {amount: string; currencyCode: string; basisQuantity: string; basisUnitId: string};
export type ItemPrice = ItemPriceDto;
export type PriceDraft = {amount: string; currencyCode: string; basisQuantity: string};
export type PriceHistoryDto = {id: string; itemId: string; userId: string; authorUsername?: string | null; changedAt: string; basisUnitLabel?: string | null; previousPrice: ItemPriceDto | null; price: ItemPriceDto | null};

export function mapItemPrice(dto: ItemPriceDto | null | undefined): ItemPrice | null {
  return dto ? {amount: dto.amount, currencyCode: dto.currencyCode, basisQuantity: dto.basisQuantity, basisUnitId: dto.basisUnitId} : null;
}

export function priceDraft(price?: ItemPrice | null): PriceDraft {
  return {amount: price ? trimDecimal(price.amount) : '', currencyCode: price?.currencyCode ?? '', basisQuantity: price ? trimDecimal(price.basisQuantity) : '1'};
}

function trimDecimal(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
}

export function calculatePriceTotal(amount: string, quantity: string): string | null {
  const price = amount.trim().replace(',', '.');
  const count = quantity.trim().replace(',', '.');
  if (!validPositiveDecimal(price, 4, 14) || !/^\d{1,15}(?:\.\d{1,3})?$/.test(count)) return null;
  const scale = (price.split('.')[1]?.length ?? 0) + (count.split('.')[1]?.length ?? 0);
  const product = (BigInt(price.replace('.', '')) * BigInt(count.replace('.', ''))).toString().padStart(scale + 1, '0');
  return scale ? trimDecimal(product.slice(0, -scale) + '.' + product.slice(-scale)) : product;
}

export function formatPriceDecimal(value: string, locale: string): string {
  const [whole, fraction] = trimDecimal(value).split('.');
  const format = new Intl.NumberFormat(locale);
  const separator = format.formatToParts(1.1).find(part => part.type === 'decimal')?.value ?? '.';
  return format.format(BigInt(whole)) + (fraction ? separator + fraction : '');
}

export function validPositiveDecimal(value: string, decimals: number, integerDigits: number): boolean {
  const normalized = value.trim().replace(',', '.');
  return new RegExp(`^\\d{1,${integerDigits}}${decimals ? `(?:\\.\\d{1,${decimals}})?` : ''}$`).test(normalized)
    && Number(normalized) > 0 && (decimals > 0 || !normalized.includes('.'));
}

@Injectable({providedIn: 'root'})
export class CurrencyService {
  private readonly http = inject(HttpClient);
  private readonly catalogs = inject(CatalogCacheService);
  private readonly language = inject(AppLanguageService);

  catalog(languageCode: string = this.language.languageCode) {
    return this.catalogs.get({id: 'currencies', url: buildApiUrl('/v1/catalogs/currencies'), schema: 2, isData: isCurrencyCatalog}, languageCode)
      .pipe(map(data => data.currencies.map(c => ({code: c.code, name: c.name, shortName: c.shortName, minorUnits: c.minorUnits, isActive: c.isActive}))));
  }

  get(languageCode?: string) {
    return forkJoin({
      currencies: this.catalog(languageCode),
      preferences: this.http.get<ApiResponse<WorkingCurrencyCodesDto>>(buildApiUrl('/v1/me/currencies/working-codes')),
    }).pipe(map(({currencies, preferences}) => ({currencies, workingCurrencyCodes: [...requireData(preferences).workingCurrencyCodes]})));
  }
  save(workingCurrencyCodes: string[]) {
    return this.http.put<ApiResponse<WorkingCurrencyCodesDto>>(buildApiUrl('/v1/me/currencies/working-codes'), {workingCurrencyCodes})
      .pipe(map(response => requireData(response)));
  }
  history(itemId: string, page: number) {
    return this.http.get<ApiResponse<PriceHistoryDto[]>>(buildApiUrl(`/v1/me/currencies/items/${itemId}/history`), {
      params: {page, pageSize: 25},
    })
      .pipe(map(response => requireData(response).map(row => ({...row, price: mapItemPrice(row.price), previousPrice: mapItemPrice(row.previousPrice)}))));
  }
}

function requireData<T>(response: ApiResponse<T>): T {
  if (!response.data) throw new Error("Missing currency response.");
  return response.data;
}
